import React from 'react';
import { useTranslation } from 'react-i18next';

import type { StructuredAnswer } from '../../../helpers/api/discussions/discussions';
import { CONCLUSION_BADGE_STYLES } from './collaboration-badges';

type StructuredAnswerBlockProps = {
  answer: StructuredAnswer;
  isOfficial?: boolean;
  embeddedInMineBubble?: boolean;
};

export default function StructuredAnswerBlock({
  answer,
  isOfficial = false,
  embeddedInMineBubble = false,
}: StructuredAnswerBlockProps) {
  const { t } = useTranslation('ClinicalCollaboration');
  const conclusionKey = answer.conclusion
    ? answer.conclusion === 'NEED_MORE_INFO'
      ? 'need_more_info'
      : answer.conclusion.toLowerCase()
    : '';

  const containerClass = embeddedInMineBubble
    ? 'mt-1.5 rounded-lg border border-blue-400/30 bg-blue-500/15 p-2.5 text-sm dark:border-blue-600 dark:bg-blue-900/50'
    : 'mt-1.5 rounded-lg border border-slate-200 bg-gray-50 p-2.5 text-sm dark:border-gray-600 dark:bg-gray-800';

  return (
    <div className={containerClass}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        {isOfficial && (
          <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {t('officialAnswer')}
          </span>
        )}
        {answer.conclusion && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              CONCLUSION_BADGE_STYLES[answer.conclusion] ?? CONCLUSION_BADGE_STYLES.UNCERTAIN
            }`}
          >
            {t(`conclusion.${conclusionKey}`, { defaultValue: answer.conclusion })}
          </span>
        )}
      </div>
      {answer.assessment && (
        <p
          className={`whitespace-pre-wrap ${
            embeddedInMineBubble
              ? 'text-white/95'
              : 'text-slate-700 dark:text-gray-200'
          }`}
        >
          {answer.assessment}
        </p>
      )}
      {answer.recommendation && (
        <p
          className={`mt-1.5 ${
            embeddedInMineBubble
              ? 'text-blue-100'
              : 'text-slate-600 dark:text-gray-400'
          }`}
        >
          → {answer.recommendation}
        </p>
      )}
    </div>
  );
}

export function ConclusionChip({ conclusion }: { conclusion: string }) {
  const { t } = useTranslation('ClinicalCollaboration');
  const key = conclusion.toLowerCase();
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        CONCLUSION_BADGE_STYLES[conclusion] ?? CONCLUSION_BADGE_STYLES.UNCERTAIN
      }`}
    >
      {t(`conclusion.${key}`, { defaultValue: conclusion })}
    </span>
  );
}
